import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

/*
  HeaderTrain — premium minimal toy train.

  Composition (reading left → right, which is the only direction of travel):
    [ L ][ O ][ V ][ E ][ ♥ ][ ENGINE → ]

  The 5 trailers each carry a soft-pastel toy block stamped with a bold
  letter (L-O-V-E) and a final white block with a black heart to match
  the brand. The engine is solid black, simple and editorial.

  Travel: always left → right, never backwards. After each crossing,
  we pause, then respawn from the left.

  Completely isolated styles — uses inline SVG with its own gradients
  and filters. No global CSS classes, no mix-blend-mode, no filter
  affects this component.
*/

const TRAIN_WIDTH = 186;
const TRAIN_HEIGHT = 30;
const CROSS_DURATION_MS = 16000;
const PAUSE_MS = 5000;

// Pastel block palette (nursery-bright but premium-muted)
const BLOCKS = [
  { letter: "L",  fill: "#FFC2CC", textFill: "#111111" }, // soft blush
  { letter: "O",  fill: "#FFD88E", textFill: "#111111" }, // warm peach
  { letter: "V",  fill: "#B8E6C1", textFill: "#111111" }, // mint
  { letter: "E",  fill: "#AFD6F0", textFill: "#111111" }, // sky
  { letter: "♥",  fill: "#FFFFFF", textFill: "#111111" }, // white card, black heart
];

const BLOCK_W = 22;
const BLOCK_GAP = 6;          // horizontal spacing between trailers
const BLOCK_START_X = 2;

function Wheel({ cx, r }) {
  return (
    <g>
      <motion.g
        animate={{ rotate: -360 }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        style={{ transformBox: "fill-box", transformOrigin: "center" }}
      >
        <circle cx={cx} cy={26} r={r} fill="#000000" />
        <circle cx={cx} cy={26} r={r * 0.42} fill="#EEEEEE" />
        <line x1={cx - r * 0.85} y1={26} x2={cx + r * 0.85} y2={26}
              stroke="#000000" strokeWidth={r * 0.22} />
        <line x1={cx} y1={26 - r * 0.85} x2={cx} y2={26 + r * 0.85}
              stroke="#000000" strokeWidth={r * 0.22} />
      </motion.g>
    </g>
  );
}

function Trailer({ x, block, index }) {
  // Trailer = black platform chassis + colored block on top + 2 wheels
  const blockX = x;
  const blockY = 6;
  const blockH = 14;
  const isHeart = block.letter === "♥";

  return (
    <g>
      {/* coupler to next wagon/engine on the right */}
      <rect
        x={blockX + BLOCK_W}
        y={22}
        width={BLOCK_GAP}
        height={1.6}
        fill="#000000"
      />

      {/* platform chassis (thin black bar beneath the block) */}
      <rect
        x={blockX - 0.5}
        y={blockY + blockH}
        width={BLOCK_W + 1}
        height={2.2}
        rx={0.6}
        fill="#000000"
      />

      {/* soft drop shadow beneath block for subtle 3D depth */}
      <rect
        x={blockX + 0.6}
        y={blockY + 0.8}
        width={BLOCK_W}
        height={blockH}
        rx={2.2}
        fill="rgba(0,0,0,0.16)"
      />

      {/* the toy block / flash card */}
      <rect
        x={blockX}
        y={blockY}
        width={BLOCK_W}
        height={blockH}
        rx={2.2}
        fill={block.fill}
        stroke="#000000"
        strokeWidth={0.6}
      />
      {/* top highlight for gentle 3D */}
      <rect
        x={blockX + 0.8}
        y={blockY + 0.8}
        width={BLOCK_W - 1.6}
        height={2.2}
        rx={1.2}
        fill="rgba(255,255,255,0.55)"
      />

      {isHeart ? (
        // Use the actual Love Exclusive brand heart logo for pixel-perfect
        // brand consistency (calligraphic tapered outline) instead of a
        // generic SVG heart path.
        <image
          href="/brand/heart.png"
          x={blockX + 3}
          y={blockY + 1.5}
          width={BLOCK_W - 6}
          height={blockH - 3}
          preserveAspectRatio="xMidYMid meet"
        />
      ) : (
        <text
          x={blockX + BLOCK_W / 2}
          y={blockY + blockH / 2 + 0.3}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="'Oswald','Helvetica Neue',Helvetica,Arial,sans-serif"
          fontWeight="900"
          fontSize={10}
          fill={block.textFill}
          style={{ letterSpacing: 0 }}
        >
          {block.letter}
        </text>
      )}

      {/* two wheels under the platform */}
      <Wheel cx={blockX + 4.5} r={2.9} />
      <Wheel cx={blockX + BLOCK_W - 4.5} r={2.9} />
    </g>
  );
}

function Engine({ x }) {
  // Premium toy-style steam locomotive — solid black body.
  // Layout (left → right, in direction of travel):
  //
  //   [ cabin + roof ][ steam dome ][ chimney ][ boiler ][ cowcatcher ▶ ]
  //                  [          DRIVE WHEEL          ][ small wheel ]
  //
  const body = "#000000";
  const win = "#FFFFFF";

  return (
    <g>
      {/* CABIN — back-left, with sloped (overhanging) roof for that toy steam look */}
      <rect x={x + 1} y={9.5} width={9}  height={9}    rx={0.8} fill={body} />
      {/* cabin roof (slightly wider, gives the silhouette its "trainy" tell) */}
      <rect x={x - 0.3} y={8.2} width={10.6} height={2}  rx={0.8} fill={body} />
      {/* cabin window */}
      <rect x={x + 2.2} y={11.3} width={6.5} height={4}  rx={0.6} fill={win} />
      {/* window mullion */}
      <rect x={x + 5.2} y={11.3} width={0.5} height={4}  fill={body} />

      {/* BOILER — long horizontal cylinder running from cabin to nose */}
      <rect x={x + 9}  y={11}   width={17.5} height={7.5} rx={3.5} fill={body} />
      {/* subtle top sheen on boiler */}
      <rect x={x + 10} y={11.4} width={15.5} height={0.9} rx={0.5}
            fill="rgba(255,255,255,0.18)" />
      {/* boiler ring detail (3 thin lines) */}
      <rect x={x + 14}   y={11} width={0.5} height={7.5} fill="rgba(255,255,255,0.18)" />
      <rect x={x + 17.5} y={11} width={0.5} height={7.5} fill="rgba(255,255,255,0.18)" />
      <rect x={x + 21}   y={11} width={0.5} height={7.5} fill="rgba(255,255,255,0.18)" />

      {/* STEAM DOME — small rounded hump just behind the chimney (classic toy detail) */}
      <ellipse cx={x + 13.5} cy={10.7} rx={1.6} ry={1.3} fill={body} />
      <rect    x={x + 12.4} y={10.7} width={2.2} height={1}  fill={body} />

      {/* CHIMNEY / SMOKESTACK — tall, with a wider flared cap */}
      <rect    x={x + 16.5} y={6}   width={2.4} height={5.3} rx={0.4} fill={body} />
      <ellipse cx={x + 17.7} cy={6}   rx={2.2} ry={0.9} fill={body} />
      <ellipse cx={x + 17.7} cy={5.4} rx={2.2} ry={0.6} fill="rgba(255,255,255,0.16)" />

      {/* COWCATCHER — angled wedge at the very front (classic locomotive nose) */}
      <polygon
        points={`
          ${x + 26.5},${11.5}
          ${x + 30.5},${15}
          ${x + 30.5},${19}
          ${x + 26.5},${19}
        `}
        fill={body}
      />
      {/* cowcatcher slats (3 thin vertical lines) */}
      <line x1={x + 27.5} y1={15} x2={x + 27.5} y2={19} stroke="rgba(255,255,255,0.25)" strokeWidth={0.3} />
      <line x1={x + 28.5} y1={15} x2={x + 28.5} y2={19} stroke="rgba(255,255,255,0.25)" strokeWidth={0.3} />
      <line x1={x + 29.5} y1={15} x2={x + 29.5} y2={19} stroke="rgba(255,255,255,0.25)" strokeWidth={0.3} />

      {/* HEADLAMP at the front of the boiler */}
      <circle cx={x + 25.5} cy={13.2} r={1.1} fill="#FFF6D5" />
      <circle cx={x + 25.5} cy={13.2} r={0.55} fill="#FFD84D" />

      {/* SMOKE PUFFS — drift backwards (up-left) above the chimney */}
      <motion.circle
        cx={x + 17.7} cy={4.2} r={1.6} fill="#FFFFFF"
        animate={{ opacity: [0, 0.85, 0], x: [0, -3, -5], y: [0, -6, -10], scale: [0.6, 1, 1.4] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
      />
      <motion.circle
        cx={x + 17.7} cy={4.2} r={1.3} fill="#FFFFFF"
        animate={{ opacity: [0, 0.7, 0], x: [0, -2, -4], y: [0, -5, -9], scale: [0.5, 0.9, 1.2] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
      />

      {/* CHASSIS — thin black bar tying the wheels together */}
      <rect x={x + 1} y={20.2} width={28} height={1.8} rx={0.5} fill={body} />

      {/* WHEELS — 1 small leading wheel + 1 larger drive wheel (classic steam loco) */}
      <Wheel cx={x + 5.5}  r={2.6} />
      <Wheel cx={x + 13.5} r={3.6} />
      <Wheel cx={x + 22}   r={2.6} />
    </g>
  );
}

function Train() {
  // Layout: 5 trailers left-to-right, then engine on the right
  const trailerXs = BLOCKS.map((_, i) => BLOCK_START_X + i * (BLOCK_W + BLOCK_GAP));
  const engineX = trailerXs[trailerXs.length - 1] + BLOCK_W + BLOCK_GAP + 2;

  return (
    <svg
      viewBox="0 0 186 32"
      width={TRAIN_WIDTH}
      height={TRAIN_HEIGHT}
      fill="none"
      data-testid="header-toy-train"
      style={{ overflow: "visible" }}
    >
      {BLOCKS.map((block, i) => (
        <Trailer key={block.letter} x={trailerXs[i]} block={block} index={i} />
      ))}
      <Engine x={engineX} />
    </svg>
  );
}

export default function HeaderTrain() {
  const [run, setRun] = useState(null);
  const [paused, setPaused] = useState(false);
  const [scale, setScale] = useState(1);
  const idRef = useRef(0);
  const timerRef = useRef(null);
  const scheduledRef = useRef(false);

  // Pause while the language dropdown is open so it never visually competes
  useEffect(() => {
    const onLang = (e) => setPaused(!!e.detail?.open);
    window.addEventListener("lex:lang-dropdown", onLang);
    return () => window.removeEventListener("lex:lang-dropdown", onLang);
  }, []);

  // Scale down on mobile so the train feels small but crisp
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setScale(mq.matches ? 0.7 : 1);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  // Kick off the first LTR run
  useEffect(() => {
    if (scheduledRef.current) return;
    scheduledRef.current = true;
    timerRef.current = setTimeout(() => {
      idRef.current += 1;
      setRun({ id: idRef.current });
    }, 1800);
    return () => {
      clearTimeout(timerRef.current);
      scheduledRef.current = false;
    };
  }, []);

  // Always loop LTR — never reverse
  const handleRunEnd = () => {
    setRun(null);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      idRef.current += 1;
      setRun({ id: idRef.current });
    }, PAUSE_MS);
  };

  return (
    <div
      aria-hidden
      data-testid="header-train-overlay"
      className="absolute left-0 right-0 pointer-events-none overflow-hidden"
      style={{ bottom: 0, height: TRAIN_HEIGHT, zIndex: 1, isolation: "isolate" }}
    >
      <AnimatePresence>
        {run && (
          <motion.div
            key={run.id}
            initial={{ x: `-${TRAIN_WIDTH + 30}px` }}
            animate={{ x: paused ? undefined : "100vw" }}
            transition={{ duration: CROSS_DURATION_MS / 1000, ease: "linear" }}
            onAnimationComplete={handleRunEnd}
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              width: TRAIN_WIDTH,
              height: TRAIN_HEIGHT,
              willChange: "transform",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                transform: `scale(${scale})`,
                transformOrigin: "center bottom",
              }}
            >
              <motion.div
                animate={{ y: [0, -0.5, 0, 0.5, 0] }}
                transition={{ duration: 1.0, repeat: Infinity, ease: "easeInOut" }}
                style={{ width: "100%", height: "100%" }}
              >
                <Train />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
